import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ClipboardText,
  Package,
  Storefront,
  Truck,
  Warning,
  ArrowRight,
  CheckCircle,
  Clock,
  FileText,
  Anchor,
  Bell,
  EnvelopeSimple,
  PaperPlaneTilt,
  Cube,
  ArrowDown,
  ChartBar
} from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import PilotWorkflowSteps from '../components/PilotWorkflowSteps';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const CURRENCY_SYMBOLS = {
  USD: '$',
  XCD: 'EC$',
  JMD: 'J$',
  TTD: 'TT$'
};

const STATUS_CONFIG = {
  requisition_approved: { label: 'Requisition Approved', color: 'bg-slate-400', icon: ClipboardText },
  po_issued: { label: 'PO Issued', color: 'bg-[#0F52BA]', icon: FileText },
  vendor_confirmed: { label: 'Vendor Confirmed', color: 'bg-[#38B2AC]', icon: CheckCircle },
  documents_uploaded: { label: 'Documents Uploaded', color: 'bg-[#F4A261]', icon: Package },
  in_transit: { label: 'In Transit', color: 'bg-[#2D6A4F]', icon: Anchor }
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterVendor, setFilterVendor] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [vendors, setVendors] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [reminderStats, setReminderStats] = useState(null);
  const [processingReminders, setProcessingReminders] = useState(false);
  const [stockOverview, setStockOverview] = useState(null);
  const [internalReqsSummary, setInternalReqsSummary] = useState(null);
  const [shipmentNotifications, setShipmentNotifications] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, [filterVendor, filterStatus]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, pipelineRes, vendorsRes, remindersRes, reminderStatsRes, stockRes, internalReqsRes, notifRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`, getAuthHeaders()),
        axios.get(`${API_URL}/api/dashboard/pipeline`, {
          ...getAuthHeaders(),
          params: {
            vendor_id: filterVendor !== 'all' ? filterVendor : undefined
          }
        }),
        axios.get(`${API_URL}/api/vendors`, getAuthHeaders()),
        axios.get(`${API_URL}/api/reminders?limit=10`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/reminders/stats`, getAuthHeaders()).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/stock-overview`, getAuthHeaders()).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/pm-portal/internal-requisitions-summary`, getAuthHeaders()).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/notifications?limit=15`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setPipeline(pipelineRes.data);
      setVendors(vendorsRes.data);
      setReminders(remindersRes.data);
      setReminderStats(reminderStatsRes.data);
      setStockOverview(stockRes.data);
      setInternalReqsSummary(internalReqsRes.data);
      // Surface shipment_update notifications (and any other freight/vendor activity).
      const recent = (notifRes.data || [])
        .filter(n => ['shipment_update', 'vendor_document_uploaded', 'vendor_confirmed'].includes(n.type))
        .slice(0, 8);
      setShipmentNotifications(recent);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessReminders = async () => {
    setProcessingReminders(true);
    try {
      const response = await axios.post(`${API_URL}/api/reminders/process`, {}, getAuthHeaders());
      toast.success(`${response.data.reminders_sent} reminder(s) sent`);
      // Refresh reminders
      const [remindersRes, reminderStatsRes] = await Promise.all([
        axios.get(`${API_URL}/api/reminders?limit=10`, getAuthHeaders()),
        axios.get(`${API_URL}/api/reminders/stats`, getAuthHeaders())
      ]);
      setReminders(remindersRes.data);
      setReminderStats(reminderStatsRes.data);
    } catch (error) {
      toast.error('Failed to process reminders');
    } finally {
      setProcessingReminders(false);
    }
  };

  const getStatusColor = (colorName) => {
    switch (colorName) {
      case 'green': return 'bg-[#2D6A4F]';
      case 'amber': return 'bg-[#F4A261]';
      case 'red': return 'bg-[#E63946]';
      default: return 'bg-slate-400';
    }
  };

  const formatCurrency = (value, currency) => {
    const symbol = CURRENCY_SYMBOLS[currency] || '$';
    return `${symbol}${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  const filteredPipeline = pipeline.filter(po => {
    if (filterStatus !== 'all' && po.status !== filterStatus) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0F52BA] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Purchasing Manager Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Welcome back, {user?.full_name}</p>
        </div>
      </div>

      {/* Pilot Mode Workflow Steps (visible only when pilot mode is active) */}
      <PilotWorkflowSteps />

      {/* Key Actions - 2 Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div 
          onClick={() => navigate('/purchase-orders')}
          className="bg-gradient-to-br from-[#3D1D72] to-[#5B2D9E] rounded-xl p-6 text-white cursor-pointer hover:shadow-lg transition-all"
          data-testid="action-view-pos"
        >
          <FileText size={32} className="mb-3" />
          <h3 className="text-xl font-bold">View & Create POs</h3>
          <p className="text-white/80 text-sm mt-1">{stats?.total_pos || 0} active purchase orders</p>
        </div>
        <div 
          onClick={() => navigate('/requisitions')}
          className="bg-white rounded-xl border border-slate-100 p-6 cursor-pointer hover:shadow-lg transition-all"
          data-testid="action-manage-pipeline"
        >
          <ClipboardText size={32} className="text-[#0F52BA] mb-3" />
          <h3 className="text-xl font-bold text-slate-800">Manage Pipeline</h3>
          <p className="text-slate-500 text-sm mt-1">{stats?.pending_conversion || 0} requisitions pending conversion</p>
        </div>
      </div>

      {/* Stats grid - Compact 4-column */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard
          title="Total Requisitions"
          value={stats?.total_requisitions || 0}
          icon={ClipboardText}
          color="blue"
          testId="stat-total-requisitions"
        />
        <StatCard
          title="Active POs"
          value={stats?.total_pos || 0}
          icon={FileText}
          color="teal"
          testId="stat-total-pos"
        />
        <StatCard
          title="In Transit"
          value={stats?.pos_in_transit || 0}
          icon={Truck}
          color="green"
          testId="stat-in-transit"
        />
        <StatCard
          title="Overdue"
          value={stats?.overdue_confirmations || 0}
          icon={Warning}
          color="red"
          testId="stat-overdue-pos"
          isAlert={stats?.overdue_confirmations > 0}
        />
      </div>

      {/* Recent Activity from FF / Vendor — surfaces shipment_update notifications immediately */}
      {shipmentNotifications.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-testid="recent-activity-panel">
          <div className="p-4 border-b border-slate-100 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#2D6A4F]/10 flex items-center justify-center">
              <Bell size={18} weight="duotone" className="text-[#2D6A4F]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Recent Activity
              </h2>
              <p className="text-xs text-slate-500">Live updates from Freight Forwarders and Vendors</p>
            </div>
            <span className="ml-auto text-xs px-2 py-1 rounded-full bg-[#2D6A4F]/10 text-[#2D6A4F] font-medium">
              {shipmentNotifications.length} new
            </span>
          </div>
          <div className="divide-y divide-slate-100">
            {shipmentNotifications.map(n => {
              const iconConfig = {
                shipment_update: { Icon: Truck, color: 'text-[#0F52BA]', bg: 'bg-[#0F52BA]/10' },
                vendor_document_uploaded: { Icon: FileText, color: 'text-[#2D6A4F]', bg: 'bg-[#2D6A4F]/10' },
                vendor_confirmed: { Icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
              }[n.type] || { Icon: Bell, color: 'text-slate-500', bg: 'bg-slate-100' };
              const { Icon, color, bg } = iconConfig;
              return (
                <button
                  key={n.id}
                  onClick={() => n.related_id && navigate(`/purchase-orders/${n.related_id}`)}
                  className="w-full text-left p-4 hover:bg-slate-50 transition-colors flex items-start gap-3"
                  data-testid={`activity-${n.id}`}
                >
                  <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={18} weight="duotone" className={color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{n.title}</p>
                    <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(n.created_at).toLocaleString('en-US', {
                        month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <ArrowRight size={16} className="text-slate-300 flex-shrink-0 mt-1" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Main Content - Full Width */}
      <div className="space-y-6">
        {/* Visual Pipeline */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Order Status Pipeline
            </h2>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#2D6A4F]"></span>
                <span className="text-xs text-slate-500">On Track</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#F4A261]"></span>
                <span className="text-xs text-slate-500">Attention</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#E63946]"></span>
                <span className="text-xs text-slate-500">Overdue</span>
              </div>
            </div>
          </div>

            {/* Pipeline stages */}
            <div className="flex items-center justify-between mb-8 overflow-x-auto pb-4">
              {Object.entries(STATUS_CONFIG).map(([key, config], idx) => {
                const count = pipeline.filter(p => p.status === key).length;
                const Icon = config.icon;
                return (
                  <div key={key} className="flex items-center">
                    <div className="flex flex-col items-center min-w-[100px]">
                      <div className={`w-12 h-12 rounded-full ${config.color} flex items-center justify-center mb-2`}>
                        <Icon size={24} weight="fill" className="text-white" />
                      </div>
                      <span className="text-xs font-medium text-slate-700 text-center">{config.label}</span>
                      <span className="text-lg font-bold text-slate-800 mt-1">{count}</span>
                    </div>
                    {idx < Object.keys(STATUS_CONFIG).length - 1 && (
                      <ArrowRight size={20} className="text-slate-300 mx-2" />
                    )}
                  </div>
                );
              })}
            </div>
        </div>

        {/* Filters and PO List */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center gap-4">
              <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {filterStatus === 'all' ? 'Active Purchase Orders' : STATUS_CONFIG[filterStatus]?.label || 'Purchase Orders'}
              </h2>
              <div className="flex-1" />
              <div className="flex items-center gap-3">
                <Select value={filterVendor} onValueChange={setFilterVendor}>
                  <SelectTrigger className="w-[180px]" data-testid="filter-vendor">
                    <SelectValue placeholder="All Vendors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Vendors</SelectItem>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[180px]" data-testid="filter-status">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
            </div>
          </div>

          {/* PO Cards */}
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPipeline.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <Package size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No purchase orders found</p>
              </div>
            ) : (
              filteredPipeline.map(po => (
                <POCard key={po.id} po={po} formatCurrency={formatCurrency} getStatusColor={getStatusColor} />
              ))
            )}
          </div>
        </div>

        {/* Reminders Sent Section */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100" data-testid="reminders-sent-section">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#7C3AED]/10 flex items-center justify-center">
                <Bell size={20} className="text-[#7C3AED]" weight="duotone" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Reminders Sent</h2>
                <p className="text-sm text-slate-500">
                  Automatic follow-ups sent to vendors and freight forwarders
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {reminderStats && (
                <div className="text-right mr-4">
                  <p className="text-sm text-slate-500">Today: <span className="font-semibold text-slate-700">{reminderStats.reminders_today}</span></p>
                  <p className="text-xs text-slate-400">This week: {reminderStats.reminders_this_week}</p>
                </div>
              )}
              <Button
                onClick={handleProcessReminders}
                disabled={processingReminders}
                className="bg-[#7C3AED] hover:bg-[#6D28D9]"
                data-testid="process-reminders-btn"
              >
                {processingReminders ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <PaperPlaneTilt size={18} className="mr-2" />
                    Check & Send Reminders
                  </>
                )}
              </Button>
            </div>
          </div>
          
          {/* Reminder Stats Summary */}
          {reminderStats && reminderStats.total_reminders > 0 && (
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex gap-6">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#0F52BA]"></span>
                <span className="text-xs text-slate-600">Vendor Confirmation: <strong>{reminderStats.by_type?.vendor_confirmation || 0}</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#F4A261]"></span>
                <span className="text-xs text-slate-600">Vendor Documents: <strong>{reminderStats.by_type?.vendor_documents || 0}</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#2D6A4F]"></span>
                <span className="text-xs text-slate-600">Freight Forwarder: <strong>{reminderStats.by_type?.freight_forwarder || 0}</strong></span>
              </div>
            </div>
          )}

          {/* Reminders List */}
          <div className="p-4">
            {reminders.length === 0 ? (
              <div className="text-center py-8">
                <EnvelopeSimple size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500 text-sm">No reminders sent yet</p>
                <p className="text-slate-400 text-xs mt-1">Click "Check & Send Reminders" to process overdue items</p>
              </div>
            ) : (
              <div className="space-y-3">
                {reminders.map(reminder => (
                  <ReminderCard key={reminder.id} reminder={reminder} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Stock Overview Section - for PM visibility */}
        {stockOverview && (user?.role === 'purchasing_manager' || user?.role === 'system_admin') && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100" data-testid="stock-overview-section">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#8B5CF6]/10 flex items-center justify-center">
                  <Cube size={20} className="text-[#8B5CF6]" weight="duotone" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">Stock Overview</h2>
                  <p className="text-sm text-slate-500">
                    Inventory items requiring attention
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-2xl font-bold text-slate-800">{stockOverview.total_items}</p>
                  <p className="text-xs text-slate-500">Total Items</p>
                </div>
                <div className={`text-right ${stockOverview.items_below_par > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  <p className="text-2xl font-bold">{stockOverview.items_below_par}</p>
                  <p className="text-xs">Below Par Level</p>
                </div>
              </div>
            </div>
            
            {stockOverview.items_below_par > 0 && (
              <div className="p-4">
                <p className="text-sm font-medium text-slate-700 mb-3">Items Requiring Reorder:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Item</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Category</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Current</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Par Level</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Shortfall</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockOverview.alerts.slice(0, 5).map((item, idx) => (
                        <tr key={item.id || idx} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="py-2 px-3 font-medium text-slate-800">{item.item_name}</td>
                          <td className="py-2 px-3 text-slate-600 capitalize">{item.category?.replace('_', ' ')}</td>
                          <td className="py-2 px-3 text-right font-medium text-red-600">
                            {item.current_stock_level?.toFixed(1)} {item.unit_of_measure}
                          </td>
                          <td className="py-2 px-3 text-right text-slate-600">
                            {item.par_level?.toFixed(1)} {item.unit_of_measure}
                          </td>
                          <td className="py-2 px-3 text-right">
                            <span className="inline-flex items-center px-2 py-0.5 rounded bg-red-100 text-red-700 text-xs font-medium">
                              <ArrowDown size={12} className="mr-1" />
                              {(item.par_level - item.current_stock_level)?.toFixed(1)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {stockOverview.alerts.length > 5 && (
                  <p className="text-xs text-slate-500 mt-3 text-center">
                    + {stockOverview.alerts.length - 5} more items below par level
                  </p>
                )}
              </div>
            )}
            
            {stockOverview.items_below_par === 0 && (
              <div className="p-6 text-center">
                <CheckCircle size={40} className="mx-auto text-green-400 mb-2" />
                <p className="text-slate-600">All inventory items are at or above par level</p>
              </div>
            )}
          </div>
        )}

        {/* Internal Requisitions Summary - for PM visibility */}
        {internalReqsSummary && internalReqsSummary.total_pending > 0 && (user?.role === 'purchasing_manager' || user?.role === 'system_admin') && (
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="internal-reqs-summary">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <ClipboardText size={20} className="text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">Internal Stock Requests</h2>
                  <p className="text-sm text-slate-500">{internalReqsSummary.total_pending} pending requests from departments</p>
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="space-y-3">
                {Object.entries(internalReqsSummary.by_department || {}).map(([dept, data]) => (
                  <div key={dept} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-800">{dept}</p>
                      <p className="text-xs text-slate-500">{data.count} pending request(s)</p>
                    </div>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                      {data.count}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-4 text-center">
                These requests are being processed by the Storekeeper
              </p>
            </div>
          </div>
        )}

        {/* Reports Card - At the bottom of PM Dashboard */}
        {(user?.role === 'purchasing_manager' || user?.role === 'system_admin') && (
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6" data-testid="reports-card">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-[#3D1D72]/10 flex items-center justify-center">
                <ChartBar size={28} className="text-[#3D1D72]" weight="duotone" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-800">Reports</h3>
                <p className="text-sm text-slate-500">Generate and download procurement reports</p>
              </div>
              <Button 
                onClick={() => window.location.href = '/reports'}
                className="bg-[#3D1D72] hover:bg-[#2D1552]"
                data-testid="open-reports-btn"
              >
                Open Reports
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color, testId, isAlert, onClick, clickHint }) => {
  const colorClasses = {
    blue: 'bg-[#0F52BA]/10 text-[#0F52BA]',
    amber: 'bg-[#F4A261]/10 text-[#B45309]',
    teal: 'bg-[#38B2AC]/10 text-[#0F766E]',
    green: 'bg-[#2D6A4F]/10 text-[#2D6A4F]',
    red: 'bg-[#E63946]/10 text-[#E63946]'
  };

  return (
    <div 
      className={`metric-card card-hover ${isAlert ? 'border-2 border-[#E63946] bg-[#E63946]/5' : ''} ${onClick ? 'cursor-pointer' : ''}`} 
      data-testid={testId}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500 mb-1">{title}</p>
          <p className={`text-3xl font-bold ${isAlert ? 'text-[#E63946]' : 'text-slate-800'}`}>{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl ${colorClasses[color]} flex items-center justify-center ${isAlert ? 'animate-pulse' : ''}`}>
          <Icon size={24} weight="duotone" />
        </div>
      </div>
      {isAlert && value > 0 && (
        <p className="text-xs text-[#E63946] mt-2 font-medium">Click to view overdue POs →</p>
      )}
      {clickHint && !isAlert && (
        <p className="text-xs text-[#B45309] mt-2 font-medium">{clickHint}</p>
      )}
    </div>
  );
};

const POCard = ({ po, formatCurrency, getStatusColor }) => {
  const statusConfig = STATUS_CONFIG[po.status] || { label: po.status, color: 'bg-slate-400' };
  
  // Check if this PO is overdue (issued, pending confirmation, over 48 hours) - only for overseas POs
  const isOverdue = po.po_type !== 'local' && 
                    po.status === 'po_issued' && 
                    po.vendor_confirmation_status === 'pending' && 
                    po.status_color === 'red';
  
  return (
    <div 
      className={`bg-slate-50 rounded-lg p-4 hover:shadow-md transition-all border ${isOverdue ? 'border-[#E63946] border-2 bg-[#E63946]/5' : 'border-slate-100'}`}
      data-testid={`po-card-${po.po_number}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-semibold text-slate-800">{po.po_number}</p>
          <p className="text-sm text-slate-500">{po.vendor_name}</p>
        </div>
        <span className={`w-3 h-3 rounded-full ${getStatusColor(po.status_color)} ${isOverdue ? 'animate-pulse' : ''}`}></span>
      </div>
      
      {/* Overdue Badge - only for overseas POs */}
      {isOverdue && (
        <div className="mb-3 px-2 py-1.5 bg-[#E63946] rounded-lg">
          <p className="text-xs font-semibold text-white flex items-center gap-1.5">
            <Warning size={14} weight="fill" />
            Overdue — Awaiting Vendor Response
          </p>
        </div>
      )}
      
      <p className="text-sm text-slate-600 mb-3 line-clamp-2">{po.key_items}</p>
      <div className="flex items-center justify-between">
        <span className="text-lg font-bold text-slate-800">
          {formatCurrency(po.total_value, po.currency)}
        </span>
        <span className={`text-xs px-2 py-1 rounded-full ${isOverdue ? 'bg-[#E63946]' : statusConfig.color} text-white`}>
          {statusConfig.label}
        </span>
      </div>
      {po.days_since_issued > 0 && po.po_type !== 'local' && (
        <p className={`text-xs mt-2 ${isOverdue ? 'text-[#E63946] font-medium' : 'text-slate-400'}`}>
          Issued {po.days_since_issued} day(s) ago
        </p>
      )}
    </div>
  );
};

const REMINDER_TYPE_CONFIG = {
  vendor_confirmation: {
    label: 'Vendor Confirmation Reminder',
    color: 'bg-[#0F52BA]',
    bgColor: 'bg-[#0F52BA]/10',
    textColor: 'text-[#0F52BA]',
    icon: Clock
  },
  vendor_documents: {
    label: 'Document Upload Reminder',
    color: 'bg-[#F4A261]',
    bgColor: 'bg-[#F4A261]/10',
    textColor: 'text-[#B45309]',
    icon: FileText
  },
  freight_forwarder_shipment: {
    label: 'Shipment Details Reminder',
    color: 'bg-[#2D6A4F]',
    bgColor: 'bg-[#2D6A4F]/10',
    textColor: 'text-[#2D6A4F]',
    icon: Truck
  }
};

const ReminderCard = ({ reminder }) => {
  const config = REMINDER_TYPE_CONFIG[reminder.reminder_type] || REMINDER_TYPE_CONFIG.vendor_confirmation;
  const Icon = config.icon;
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div 
      className={`flex items-center gap-4 p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-all`}
      data-testid={`reminder-card-${reminder.id}`}
    >
      <div className={`w-10 h-10 rounded-lg ${config.bgColor} flex items-center justify-center flex-shrink-0`}>
        <Icon size={20} className={config.textColor} weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-xs px-2 py-0.5 rounded-full ${config.color} text-white`}>
            {config.label}
          </span>
          <span className="text-xs text-slate-400">{formatDate(reminder.sent_at)}</span>
        </div>
        <p className="text-sm font-medium text-slate-800 truncate">
          PO: {reminder.po_number}
        </p>
        <p className="text-xs text-slate-500 truncate">
          Sent to: {reminder.recipient_name} ({reminder.recipient_email})
        </p>
      </div>
      <div className="flex-shrink-0">
        <EnvelopeSimple size={20} className="text-slate-400" />
      </div>
    </div>
  );
};

export default DashboardPage;
